import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import TableComponent from '../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../Components/snackBar/snackBar';
import { LoadingSpinner } from '../../Components/spinner/spinner';
import { API_BASE_URL } from '../../config';
import api from '../../api/axiosInterceptor';
import axios from 'axios';

export const AdminScamNewsComponent = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAct, setSelectedAct] = useState(null);

  const [formData, setFormData] = useState({
    subject: '',
    date_of_issue: '',
    language: '',
    link: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAct, setDeleteAct] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'subject', field: 'subject', headerName: 'Subject', width: 250 },
    { id: 'dateOfIssue', field: 'dateOfIssue', headerName: 'Date Of Issue', width: 150 },
    { id: 'language', field: 'language', headerName: 'Language', width: 150 },
    {
      id: 'links',
      field: 'links',
      headerName: 'Links',
      width: 120,
      renderCell: (params) =>
        params.value ? (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            View
          </a>
        ) : (
          'No Link'
        ),
    },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchActs();
  }, []);

  const fetchActs = () => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/home-notification/scam-news/`)
      .then((response) => {
        const apiData = response.data.map((item, index) => ({
          id: item.id,
          slNo: index + 1,
          subject: item.subject,
          dateOfIssue: item.date_of_issue,
          language: item.language,
          links: item.link || '',
        }));
        setRows(apiData);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch scam-news data:', error);
        setSnackbarMessage('Failed to load scam-news data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAddAct = () => {
    setIsEditMode(false);
    setFormData({ subject: '', date_of_issue: '', language: '', link: '' });
    setFormErrors({});
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleEdit = (act) => {
    setIsEditMode(true);
    setSelectedAct(act);
    setFormData({
      subject: act.subject,
      date_of_issue: act.dateOfIssue,
      language: act.language,
      link: act.links,
    });
    setFormErrors({});
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (act) => {
    setDeleteAct(act);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api
      .delete(`/home-notification/scam-news/${deleteAct.id}/`)
      .then(() => {
        setSnackbarMessage('scam-news deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchActs();
      })
      .catch((error) => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete scam-news.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteAct(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.subject?.trim()) errors.subject = 'Subject is required';
    if (!formData.date_of_issue) errors.date_of_issue = 'Date of issue is required';
    if (!formData.language?.trim()) errors.language = 'Language is required';
    if (!formData.link?.trim()) errors.link = 'Link is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      subject: formData.subject,
      date_of_issue: formData.date_of_issue,
      language: formData.language,
      link: formData.link,
    };

    try {
      setUploading(true);
      setUploadProgress(0);

      if (isEditMode) {
        await api.patch(`/home-notification/scam-news/${selectedAct.id}/`, payload);
        setSnackbarMessage('scam-news updated successfully.');
      } else {
        await api.post(`/home-notification/scam-news/`, payload);
        setSnackbarMessage('scam-news added successfully.');
      }

      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchActs();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit scam-news.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return <LoadingSpinner size={30} />;
  }

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Scams News
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddAct} disabled={uploading}>
          Add
        </Button>
      </Box>

      <TableComponent
        columns={columns}
        rows={rows}
        manageColumnStatus
        enableSort
        sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' } }}
      />

      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)}>
        <DialogTitle>{isEditMode ? 'Edit scam-news' : 'Add scam-news'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Subject"
            fullWidth
            value={formData.subject}
            error={!!formErrors.subject}
            helperText={formErrors.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Date Of Issue"
            type="date"
            fullWidth
            value={formData.date_of_issue}
            error={!!formErrors.date_of_issue}
            helperText={formErrors.date_of_issue}
            onChange={(e) => setFormData({ ...formData, date_of_issue: e.target.value })}
            InputLabelProps={{ shrink: true }}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Language"
            fullWidth
            value={formData.language}
            error={!!formErrors.language}
            helperText={formErrors.language}
            onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Link (URL)"
            fullWidth
            value={formData.link}
            error={!!formErrors.link}
            helperText={formErrors.link}
            onChange={(e) => setFormData({ ...formData, link: e.target.value })}
            disabled={uploading}
          />
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
                Processing... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this scam-news?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
